import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, DatePicker, Form, Input, Switch, Table } from 'antd';
import {
  createCoupon,
  getCouponById,
  getProducts,
  getSubsidiaries,
  getTreatments,
  updateCoupon,
} from '../../../../appRedux/actions';
import {
  buttonLabel,
  cardConfig,
  couponTypeConst,
  couponTypeSingularConst,
  couponsFormConfig,
  dateFormatMx,
  formConfig,
  lengthValidation,
  masks,
  messages,
  minLengthValidation,
  placeHolders,
} from '../../../../constants';
import {
  CancelButton,
  CustomSelect,
  NumericInput,
} from '../../../uiComponents';
import TextArea from 'antd/lib/input/TextArea';
import { MaskedInput } from 'antd-mask-input';
import { formatPrice } from '../../../../util/formatPrice';
import moment from 'moment';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

export const CouponForm = () => {
  const [res, setRes] = useState();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { couponById } = useSelector((state) => state.coupon);
  const { subsidiaries } = useSelector((state) => state.subsidiary);
  const { treatments } = useSelector((state) => state.treatment);
  const { products } = useSelector((state) => state.product);
  const [couponType, setCouponType] = useState(null);
  const [treatment, setTreatment] = useState(null);
  const [product, setProduct] = useState(null);
  const [discount, setDiscount] = useState(null);
  const [status, setStatus] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [multipleTreatment, setMultipleTreatment] = useState([]);
  const [multipleProduct, setMultipleProduct] = useState([]);
  const [multipleDiscounts, setMultipleDiscounts] = useState([]);
  const [mixItems, setMixItems] = useState([]);

  useEffect(() => {
    if (id) dispatch(getCouponById(id));

    dispatch(getSubsidiaries({ status: '1' }));
    dispatch(getTreatments({ status: '1' }));
    dispatch(
      getProducts({
        status: 1,
        productStatus: 'approved',
        productType: 'Cosmetólogo/Venta',
        withDeleted: 'false',
      })
    );
  }, [id, dispatch]);

  // TODO : Refactor this function to improve readability
  const calculateMultipleDiscounts = useCallback(() => {
    const getDiscounts = (items, itemType, priceKey) =>
      items
        .map((itemId) => {
          const item = itemType.find((i) => i.id === itemId);
          if (item) {
            const regularPrice = item[priceKey] || 0;
            const discountPrice =
              regularPrice - regularPrice * (discount / 100);
            return {
              id: item.id,
              name: item.name,
              regularPrice,
              discountPrice,
              discount,
            };
          }
          return null;
        })
        .filter(Boolean);

    let discounts = [];
    if (couponType === couponTypeSingularConst.multipleTreatment) {
      discounts = getDiscounts(multipleTreatment, treatments, 'price');
    } else if (couponType === couponTypeSingularConst.multipleProduct) {
      discounts = getDiscounts(multipleProduct, products, 'salePrice');
    } else if (couponType === couponTypeSingularConst.mix) {
      const mixDiscounts = mixItems
        .map(({ id, type }) => {
          if (type === couponTypeSingularConst.treatment) {
            const item = treatments.find((treatment) => treatment.id === id);
            if (!item) return null;

            const regularPrice = item.price || 0;
            const discountPrice =
              regularPrice - regularPrice * (discount / 100);

            return {
              name: item.name,
              regularPrice,
              discountPrice,
              discount,
            };
          }

          if (type === couponTypeSingularConst.product) {
            const item = products.find((product) => product.id === id);
            if (!item) return null;

            const regularPrice = item.salePrice || 0;
            const discountPrice =
              regularPrice - regularPrice * (discount / 100);

            return {
              id: `p-${id}`,
              name: item.name,
              regularPrice,
              discountPrice,
              discount,
            };
          }

          return null;
        })
        .filter(Boolean);

      discounts = mixDiscounts;
    }

    setMultipleDiscounts(discounts);
  }, [
    couponType,
    multipleTreatment,
    multipleProduct,
    discount,
    treatments,
    products,
    mixItems,
  ]);

  useEffect(() => {
    if (couponType === couponTypeSingularConst.orderAmount) {
      setMultipleDiscounts([]);
      return;
    }

    const hasMultiple =
      multipleTreatment.length || multipleProduct.length || mixItems.length;

    if (hasMultiple && discount !== null) {
      calculateMultipleDiscounts();
    } else {
      setMultipleDiscounts([]);
    }
  }, [
    multipleTreatment,
    multipleProduct,
    mixItems,
    discount,
    calculateMultipleDiscounts,
    couponType,
  ]);

  useEffect(() => {
    if (!product && !treatment && couponById) {
      form.setFieldsValue({
        productId: couponById?.product?.id,
        treatmentId: couponById?.treatment?.id,
      });
    } else {
      form.setFieldsValue({
        regularPrice: formatPrice(0),
        discountPrice: formatPrice(0),
      });
    }
  }, [couponById, couponType, form, product, treatment]);

  useEffect(() => {
    if (product || treatment) {
      const regPrice = product
        ? products.find((prod) => {
            return prod.id === product;
          })?.salePrice || 0
        : treatments.find((treat) => {
            return treat.id === treatment;
          })?.price || 0;

      const discountPrice = regPrice - regPrice * (discount / 100);
      form.setFieldsValue({
        regularPrice: formatPrice(regPrice),
        discountPrice: formatPrice(discountPrice),
      });
    }
  }, [form, product, products, treatment, treatments, discount]);

  useEffect(() => {
    if (couponById && id) {
      setStatus(couponById?.status);
      setCouponType(couponById.type);
      setDiscount(couponById.discount);

      const regPrice = couponById?.product
        ? couponById?.product?.salePrice || 0
        : couponById?.treatment?.price || 0;

      const discountPrice = formatPrice(
        regPrice - regPrice * (couponById.discount / 100)
      );

      form.setFieldsValue({
        ...couponById,
        expireDate: moment(couponById?.expireDate),
        branchesIds: couponById?.branches?.map((branch) => branch.id),
        productId: couponById?.product?.id,
        treatmentId: couponById?.treatment?.id,
        regularPrice: formatPrice(regPrice),
        discountPrice,
      });

      if (couponById.treatments && couponById.treatments.length) {
        const selectedTreatments = couponById.treatments.map(
          (treatment) => treatment.id
        );
        setMultipleTreatment(selectedTreatments);

        form.setFieldsValue({
          treatmentsIds: selectedTreatments,
        });
      }

      if (couponById.products && couponById.products.length) {
        const selectedProducts = couponById.products.map(
          (product) => product.id
        );
        setMultipleProduct(selectedProducts);

        form.setFieldsValue({
          productsIds: selectedProducts,
        });
      }

      if (
        (couponById.type === couponTypeSingularConst.multipleTreatment &&
          couponById.treatments?.length) ||
        (couponById.type === couponTypeSingularConst.multipleProduct &&
          couponById.products?.length)
      ) {
        calculateMultipleDiscounts();
      }
    }
  }, [couponById, form, id]);

  useEffect(() => {
    form.setFieldsValue({
      discount: discount,
    });
  }, [discount, form]);

  useEffect(() => {
    if ([200, 201].includes(res)) {
      navigate('/dashboard/cupones');
    }
  }, [navigate, res]);

  const onHandledSubmit = async (values) => {
    setIsLoading(true);
    values.discount = parseFloat(values.discount);

    if (couponType === couponTypeSingularConst.mix) {
      values.items = mixItems;
    }

    if (id) {
      setRes(await dispatch(updateCoupon(id, values)));
    } else {
      setRes(await dispatch(createCoupon(values)));
    }
    setIsLoading(false);
  };

  const discountColumns = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Precio Regular',
      dataIndex: 'regularPrice',
      key: 'regularPrice',
      render: (price) => formatPrice(price),
    },
    {
      title: 'Precio con Descuento',
      dataIndex: 'discountPrice',
      key: 'discountPrice',
      render: (price) => formatPrice(price),
    },
    {
      title: 'Descuento',
      dataIndex: 'discount',
      key: 'discount',
      render: (discount) => `${discount}%`,
    },
  ];

  const treatmentOptions = useMemo(() => {
    return (
      treatments?.map(({ id, name }) => ({
        value: id,
        label: name,
      })) || []
    );
  }, [treatments]);

  const productOptions = useMemo(() => {
    return (
      products?.map(({ id, name }) => ({
        value: id,
        label: name,
      })) || []
    );
  }, [products]);

  const mixedOptions = useMemo(() => {
    const treatmentOpts =
      treatments?.map(({ id, name }) => ({
        value: id,
        label: `${name} (Tratamiento)`,
        type: couponTypeSingularConst.treatment,
      })) || [];

    const productOpts =
      products?.map(({ id, name }) => ({
        value: id,
        label: `${name} (Producto)`,
        type: couponTypeSingularConst.product,
      })) || [];

    return [...treatmentOpts, ...productOpts];
  }, [treatments, products]);

  const isMultipleType =
    couponType === couponTypeSingularConst.multipleTreatment ||
    couponType === couponTypeSingularConst.multipleProduct ||
    couponType === couponTypeSingularConst.mix;

  const isFixedAmount = couponType === couponTypeSingularConst.orderAmount;

  return (
    <>
      <Card size="small" bordered={true} style={cardConfig}>
        <Form
          {...formConfig}
          initialValues={{ remember: true }}
          autoComplete="off"
          onFinish={onHandledSubmit}
          form={form}
        >
          <Form.Item
            rules={[
              { required: true, message: messages.required('Código') },
              { min: 5, message: minLengthValidation('Código', 5) },
              { max: 15, message: lengthValidation('Código', 15) },
            ]}
            name="code"
            label="Código"
          >
            <Input />
          </Form.Item>
          <Form.Item
            rules={[
              { required: true, message: messages.required('Nombre') },
              { max: 50, message: lengthValidation('Nombre', 50) },
            ]}
            name="name"
            label="Nombre"
          >
            <Input />
          </Form.Item>
          <Form.Item
            rules={[
              { required: true, message: messages.required('Descripción') },
            ]}
            name="description"
            label="Descripción"
          >
            <TextArea />
          </Form.Item>
          <Form.Item
            rules={[
              { required: true, message: messages.required('Sucursales') },
            ]}
            name="branchesIds"
            label="Válido en"
          >
            <CustomSelect
              placeholder={placeHolders.branch}
              mode="multiple"
              options={subsidiaries?.map(({ id, name }) => ({
                value: id,
                label: name,
              }))}
            />
          </Form.Item>
          <Form.Item
            label="Límite de usos"
            name="limit"
            rules={[
              {
                required: true,
                message: messages.required('Límite de usos'),
              },
            ]}
          >
            <NumericInput />
          </Form.Item>
          <Form.Item
            label="Límite por usuario"
            name="useLimitByUser"
            rules={[
              {
                required: true,
                message: messages.required('Límite por usuario'),
              },
            ]}
          >
            <NumericInput />
          </Form.Item>
          <Form.Item
            rules={[{ required: true, message: messages.required('Tipo') }]}
            name="type"
            label="Tipo"
          >
            <CustomSelect
              onChange={setCouponType}
              placeholder={placeHolders.type}
              options={couponTypeConst}
              disabled={id ? true : false}
            ></CustomSelect>
          </Form.Item>

          {couponType === couponTypeSingularConst.treatment && (
            <Form.Item
              rules={[
                { required: true, message: messages.required('Tratamiento') },
              ]}
              name="treatmentId"
              label="Tratamiento"
            >
              <CustomSelect
                onChange={setTreatment}
                placeholder={placeHolders.treatment}
                options={treatmentOptions}
                disabled={id ? true : false}
              />
            </Form.Item>
          )}

          {couponType === couponTypeSingularConst.product && (
            <Form.Item
              rules={[
                { required: true, message: messages.required('Producto') },
              ]}
              name="productId"
              label="Producto"
            >
              <CustomSelect
                onChange={setProduct}
                placeholder={placeHolders.product}
                options={productOptions}
                disabled={id ? true : false}
              />
            </Form.Item>
          )}

          {couponType === couponTypeSingularConst.multipleTreatment && (
            <Form.Item
              rules={[
                { required: true, message: messages.required('Tratamiento') },
              ]}
              name="treatmentsIds"
              label="Tratamiento"
            >
              <CustomSelect
                onChange={setMultipleTreatment}
                placeholder={placeHolders.treatment}
                options={treatmentOptions}
                disabled={!!id}
                mode="multiple"
              />
            </Form.Item>
          )}

          {couponType === couponTypeSingularConst.mix && (
            <Form.Item
              rules={[
                {
                  required: true,
                  message: messages.required('Tratamientos o productos'),
                },
              ]}
              name="mixIds"
              label="Tratamientos o productos"
            >
              <CustomSelect
                placeholder={placeHolders.mix}
                options={mixedOptions}
                mode="multiple"
                onChange={(values, options) => {
                  const parsed = options.map((opt) => ({
                    id: opt.value,
                    type: opt.type,
                  }));

                  setMixItems(parsed);
                }}
              />
            </Form.Item>
          )}

          {couponType === couponTypeSingularConst.multipleProduct && (
            <Form.Item
              rules={[
                { required: true, message: messages.required('Producto') },
              ]}
              name="productsIds"
              label="Producto"
            >
              <CustomSelect
                onChange={setMultipleProduct}
                placeholder={placeHolders.product}
                options={productOptions}
                disabled={!!id}
                mode="multiple"
              />
            </Form.Item>
          )}

          <Form.Item
            {...couponsFormConfig}
            name="discount"
            label={isFixedAmount ? 'Monto de descuento' : 'Descuento'}
            rules={[
              { required: true, message: messages.required('Descuento') },
            ]}
          >
            {!id &&
              (isFixedAmount ? (
                <NumericInput
                  min={0}
                  onChange={(value) => setDiscount(value)}
                  placeholder={placeHolders.orderAmount}
                />
              ) : (
                <MaskedInput
                  onChange={(value) => {
                    setDiscount(
                      value.maskedValue !== '_.%'
                        ? parseFloat(value.maskedValue)
                        : 0
                    );
                  }}
                  mask={masks.numPercent}
                />
              ))}

            {id && discount && (
              <Input
                disabled
                defaultValue={
                  isFixedAmount
                    ? formatPrice(couponById?.discount)
                    : `${couponById?.discount}%`
                }
              />
            )}
          </Form.Item>

          {isMultipleType && !isFixedAmount ? (
            <Form.Item label="Precios con Descuento">
              <Table
                size="small"
                columns={discountColumns}
                dataSource={multipleDiscounts}
                pagination={false}
                rowKey="id"
                locale={{
                  emptyText:
                    'Selecciona elementos y establece un descuento para ver los precios',
                }}
              />
            </Form.Item>
          ) : (
            <>
              <Form.Item
                {...couponsFormConfig}
                label="Precio regular"
                name="regularPrice"
              >
                <Input disabled />
              </Form.Item>
              <Form.Item
                {...couponsFormConfig}
                label="Precio c/descuento"
                name="discountPrice"
              >
                <Input disabled />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="expireDate"
            label="Vigencia"
            rules={[{ required: true, message: messages.required('Vigencia') }]}
          >
            <DatePicker style={{ width: '100%' }} format={dateFormatMx} />
          </Form.Item>
          {id && (
            <Form.Item label="Status" name="status">
              <Switch
                checked={status}
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
                onChange={setStatus}
              />
            </Form.Item>
          )}

          <Button
            type="primary"
            htmlType="submit"
            className="btn-form"
            loading={isLoading}
          >
            {buttonLabel.save}
          </Button>
        </Form>
        <CancelButton path="/dashboard/cupones" />
      </Card>
    </>
  );
};
